import { absoluteUrl, canonicalUrl, organizationSchema, SITE_NAME, SITE_URL } from "@/lib/seo";
import type { MarketingMetadata } from "./marketing-schema";

/**
 * The article template (`template: article` in an MDX file's frontmatter): a
 * byline with a real photo, "On this page", key takeaways, a cover, charts,
 * the questions, a waitlist box and cards for the pages to read next.
 *
 * Covers, share images and card images are drawn by `scripts/article-covers.mjs`
 * into `public/articles/`, named after the page's path: /feature/linkedin-api is
 * feature-linkedin-api.webp (the cover), .jpg (og:image) and -thumb.webp (its card).
 */

export const AUTHORS = {
  // Photos: the bavlio.com homepage's founder photos (bavlio-frontend
  // public/landing/sam.jpg and adam.jpg), cut to 176px around the face. Names,
  // roles and profiles match the Organization's founders on bavlio.com; the
  // bios are the approved founder bios (DECISIONS.md, Oct 1 2026).
  "sam-cannas": {
    name: "Sam Cannas",
    role: "CTO and co-founder",
    bio: "Coding since 14.",
    photo: "/authors/sam-cannas.webp",
    sameAs: ["https://www.linkedin.com/in/cannas"],
  },
  "adam-badar": {
    name: "Adam Badar",
    role: "Founder and CEO",
    bio: "Made 5,000+ cold calls at 17.",
    photo: "/authors/adam-badar.webp",
    sameAs: ["https://www.linkedin.com/in/adam-badar/"],
  },
} as const;

export type AuthorId = keyof typeof AUTHORS;
export type Author = (typeof AUTHORS)[AuthorId];

export const COVER_SIZE = { width: 1600, height: 840 } as const;
export const SHARE_SIZE = { width: 1200, height: 630 } as const;
export const THUMB_SIZE = { width: 480, height: 252 } as const;

function imageName(url: string): string {
  return url.slice(1).replaceAll("/", "-");
}
export const coverOf = (url: string) => `/articles/${imageName(url)}.webp`;
export const shareImageOf = (url: string) => `/articles/${imageName(url)}.jpg`;
export const thumbOf = (url: string) => `/articles/${imageName(url)}-thumb.webp`;

/** Minutes to read `text` at 230 words a minute, at least one. */
export function readMinutes(text: string): number {
  const words = text
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/\]\([^)]*\)/g, "]")
    .split(/\s+/)
    .filter((word) => /[a-z0-9]/i.test(word)).length;
  return Math.max(1, Math.ceil(words / 230));
}

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/** "2026-10-14" as "October 14, 2026", the same on the server and in the browser. */
export function longDate(iso: string): string {
  const [year, month, day] = iso.split("-").map(Number);
  return `${MONTHS[month - 1]} ${day}, ${year}`;
}

export type RelatedCard = {
  url: string;
  title: string;
  description: string;
  label: string;
  thumb: string;
};

/** The labels on the cards under "Keep reading". */
const CARD_LABELS: Record<string, string> = {
  feature: "Feature",
  blog: "Blog",
  docs: "Docs",
  compare: "Compare",
  audience: "Audience",
};

export function cardLabel(url: string): string {
  return CARD_LABELS[url.split("/")[1]] ?? "Bavimail";
}

export type ArticleData = {
  author: Author;
  /** The page's short name, for the breadcrumb. */
  crumb: string;
  takeaways: string[];
  availability: { channel: string; status: "Live" | "Early access" | "Waitlist" }[];
  /** The page's one action: the box's line, the bar's shorter line on a phone, the button. */
  action: { title: string; bar: string; label: string; href: string };
  related: RelatedCard[];
  readMinutes: number;
  datePublished: string;
  dateModified: string;
  cover: string;
  coverAlt: string;
  shareImage: string;
};

/** Everything the template needs beyond the MDX body, or why the page can't use it. */
export function articleData(
  url: string,
  data: MarketingMetadata,
  text: string,
  cardOf: (url: string) => { title: string; description: string } | undefined,
): ArticleData {
  const missing = (
    ["author", "coverAlt", "takeaways", "action", "datePublished", "dateModified"] as const
  )
    .filter((key) => !data[key])
    .join(", ");
  if (missing) throw new Error(`${url} uses the article template without ${missing}`);
  return {
    author: AUTHORS[data.author!],
    crumb: data.cardTitle ?? data.title,
    takeaways: data.takeaways!,
    availability: data.availability ?? [],
    action: data.action!,
    related: (data.related ?? []).map((link) => {
      const href = typeof link === "string" ? link : link.href;
      const known = cardOf(href);
      if (!known)
        throw new Error(`${url} links to ${href} under "Keep reading", which has no page`);
      const own: { title?: string; description?: string } = typeof link === "string" ? {} : link;
      return {
        url: href,
        title: own.title ?? known.title,
        description: own.description ?? known.description,
        label: cardLabel(href),
        thumb: thumbOf(href),
      };
    }),
    readMinutes: readMinutes(
      [data.takeaways!.join(" "), text, ...(data.faqs ?? []).map((faq) => faq.answer)].join(" "),
    ),
    datePublished: data.datePublished!,
    dateModified: data.dateModified!,
    cover: coverOf(url),
    coverAlt: data.coverAlt!,
    shareImage: shareImageOf(url),
  };
}

/**
 * The article's structured data: written by the byline's author as a Person,
 * published by Bavimail, with the cover as its image. A blog post is a
 * BlogPosting, a feature page an Article.
 */
export function articleSchema({
  type,
  url,
  title,
  description,
  keywords,
  article,
}: {
  type: "Article" | "BlogPosting";
  url: string;
  title: string;
  description: string;
  keywords?: string[];
  article: ArticleData;
}) {
  const page = canonicalUrl(url);
  return {
    "@context": "https://schema.org",
    "@type": type,
    headline: title,
    description,
    url: page,
    image: absoluteUrl(article.shareImage),
    inLanguage: "en",
    datePublished: article.datePublished,
    dateModified: article.dateModified,
    author: {
      "@type": "Person",
      name: article.author.name,
      jobTitle: article.author.role,
      image: absoluteUrl(article.author.photo),
      sameAs: article.author.sameAs,
      worksFor: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
    },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_URL,
      logo: { "@type": "ImageObject", url: organizationSchema().logo },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": page },
    ...(keywords?.length ? { keywords: keywords.join(", ") } : {}),
  };
}
